import { z } from 'zod';
import { uuidSchema, bloodTypeSchema } from './common.schema';
import { PaginationQuerySchema } from './pagination.schema';
import { MilkProviderAlcoholUse, MilkProviderDietType } from './milkProviderHealthRecords.schema';

/**
 * Milk Provider (Mother / Donor) Schemas
 * Aligned with the `milk_providers` Prisma model (snake_case).
 */

// ─── Shared person-type enum ──────────────────────────────────────────────────

export const PersonTypeSchema = z.enum(['MOTHER', 'DONOR', 'BOTH']);
export type PersonType = z.infer<typeof PersonTypeSchema>;

// ─── Inline exclusion block ───────────────────────────────────────────────────

export const ExclusionTypeSchema = z.enum([
  'MEDICATION',
  'INFECTION',
  'SURGERY',
  'MASTITIS',
  'POSITIVE_SEROLOGY',
  'OTHER',
]);
export type ExclusionType = z.infer<typeof ExclusionTypeSchema>;

// ─── Child status enum (DONOR only) ───────────────────────────────────────────

export const ChildStatusEnum = z.enum(['FULL_TERM', 'PREMATURE', 'DECEASED', 'UNKNOWN']);
export type ChildStatus = z.infer<typeof ChildStatusEnum>;

export const ChildDetailSchema = z.object({
  child_dob:    z.string().date().refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'Child date of birth cannot be in the future' }).describe('Child date of birth (YYYY-MM-DD)'),
  child_status: ChildStatusEnum.describe('Child status: FULL_TERM | PREMATURE | DECEASED | UNKNOWN'),
});
export type ChildDetail = z.infer<typeof ChildDetailSchema>;

/**
 * Inline exclusion block — optionally embedded inside a register/update request.
 * When provided, a milk_provider_exclusions row is created/updated atomically.
 */
export const InlineExclusionSchema = z.object({
  exclusion_id:         uuidSchema.optional().describe('UUID of an existing exclusion to update — omit to create a new one'),
  exclusion_type:       ExclusionTypeSchema.optional().default('OTHER'),
  exclusion_start_date: z.string().date().describe('Start date of exclusion (YYYY-MM-DD)'),
  exclusion_end_date:   z.string().date().optional().nullable().describe('End date of exclusion (YYYY-MM-DD)'),
  exclusion_reason:     z.string().optional().nullable().describe('Clinical reason for exclusion'),
  recorded_by:          uuidSchema.describe('UUID of the staff member recording the exclusion'),
  notes:                z.string().optional().nullable(),
});
export type InlineExclusion = z.infer<typeof InlineExclusionSchema>;

/**
 * Inline consent block — optionally embedded inside a register request.
 * When provided, a clinical_consent_records row is created atomically
 * alongside the milk_providers and subject_pseudonyms rows,
 * with subject_type = 'MILK_PROVIDER'.
 */
export const InlineConsentSchema = z.object({
  purpose_code:          z.string().min(1).max(50).describe('Code from consent_purposes table (e.g. DONOR_MILK_USAGE)'),
  action:                z.enum(['GRANTED', 'WITHDRAWN', 'REFUSED']).describe('Consent action'),
  consent_method:        z.enum(['WRITTEN_FORM', 'VERBAL', 'ELECTRONIC']).describe('How consent was captured'),
  recorded_by:           uuidSchema.describe('UUID of the staff member recording consent'),
  digital_signature_ref: z.string().max(500).optional().nullable(),
  consent_document_ref:  z.string().max(500).optional().nullable(),
  consent_version:       z.string().max(20).optional().nullable(),
  is_proxy_consent:      z.boolean().optional().default(false),
  guardian_name:         z.string().max(200).optional().nullable(),
  guardian_relationship: z.string().max(30).optional().nullable(),
  consent_form_ref:      z.string().max(200).optional().nullable().describe('Reference to the physical consent form'),
  granted_at:            z.string().datetime({ offset: true, local: true }).nullable().describe('ISO 8601 timestamp — defaults to now()'),
  expires_at:            z.string().datetime({ offset: true, local: true }).nullable(),
  notes:                 z.string().optional().nullable(),
});
export type InlineConsent = z.infer<typeof InlineConsentSchema>;

// ─── List Item ────────────────────────────────────────────────────────────────

/** Cognito user details merged into DONOR / BOTH list items. undefined = enrichment not attempted (MOTHER-only or no hospital_id); null = attempted but pool unconfigured or lookup failed. */
export const MilkProviderCognitoInfoSchema = z.object({
  username:           z.string().describe('Cognito username (typically the donor email)'),
  userStatus:         z.string().describe('Cognito UserStatus (CONFIRMED | UNCONFIRMED | FORCE_CHANGE_PASSWORD | …)'),
  enabled:            z.boolean().describe('Whether the Cognito account is enabled'),
  confirmationStatus: z.string().describe('Confirmation status — mirrors userStatus'),
}).nullable();
export type MilkProviderCognitoInfo = z.infer<typeof MilkProviderCognitoInfoSchema>;

export const MilkProviderListItemSchema = z.object({
  person_id:        uuidSchema,
  hospital_id:      uuidSchema,
  name:             z.string().describe('Full name: first_name + last_name'),
  role:             z.string().describe('person_type (MOTHER | DONOR)'),
  mrn:              z.string().nullable(),
  dob:              z.coerce.date().nullable().describe('date_of_birth'),
  screening_status: z.string().describe('Health screening status'),
  is_active:        z.boolean().describe('Whether the provider is active (true) or discharged/inactive (false)'),
  is_active_donor:  z.boolean().optional().nullable().describe('Whether the donor role is active; false when donor role was discharged from a BOTH provider; null for legacy rows predating this field'),
  is_active_mother: z.boolean().optional().nullable().describe('Whether the mother role is active; false when mother role was discharged from a BOTH provider; null for legacy rows predating this field'),
  cognito_info:     MilkProviderCognitoInfoSchema.optional().describe('Cognito account details — object: enriched; null: lookup attempted but unavailable/failed; undefined: MOTHER-only record or enrichment skipped (no hospital_id)'),

});
export type MilkProviderListItem = z.infer<typeof MilkProviderListItemSchema>;

// ─── List Query ───────────────────────────────────────────────────────────────

export const MilkProviderListQuerySchema = PaginationQuerySchema.extend({
  role:              z.enum(['MOTHER', 'DONOR', 'BOTH', 'MOTHER_AND_BOTH', 'DONOR_AND_BOTH']).optional().describe('Filter: MOTHER (only mothers) | DONOR (only donors) | BOTH (only both) | MOTHER_AND_BOTH | DONOR_AND_BOTH'),
  search:            z.string().trim().min(1).max(100).optional().describe('Search by first/last name or MRN'),
  sort_by:           z.enum(['name', 'dob', 'mrn', 'role']).optional().default('name').describe('Sort field: name | dob | mrn | role'),
  sort_order:        z.enum(['asc', 'desc']).optional().default('asc').describe('Sort direction: asc | desc'),
  limit:             z.coerce.number().int().min(1).optional().describe('Max results to return. Omit for full list.'),
  is_active:         z.preprocess(
    (v) => (v === 'false' ? false : v === 'true' ? true : v),
    z.union([z.boolean(), z.literal('all')]).optional().default(true),
  ).describe('Filter by active status. true (default) = active providers; false = discharged/inactive providers; "all" = both active and discharged'),
  // Filter: donor/provider screening status (array); accepts a single value or multiple values.
  screening_status:  z.preprocess(
    (val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
    z.array(z.enum(['APPROVED', 'CLEARED', 'PENDING', 'REJECTED', 'EXPIRED', 'ON_HOLD', 'NOT_REQUIRED'])).optional(),
  ).describe('Filter by donor/provider screening status. Accepts a single value or multiple values: APPROVED | CLEARED | PENDING | REJECTED | EXPIRED | ON_HOLD | NOT_REQUIRED'),
  from: z.coerce.date().optional().describe('Filter by DOB on or after this date (ISO 8601 timestamp, timezone-aware)'),
  to: z.coerce.date().optional().describe('Filter by DOB on or before this date (ISO 8601 timestamp, timezone-aware)'),
});
export type MilkProviderListQuery = z.infer<typeof MilkProviderListQuerySchema>;

// ─── Detail (single provider) ───────────────────────────────────────────────

export const MilkProviderDetailSchema = z.object({
  person_id:                      uuidSchema,
  hospital_id:                    uuidSchema,
  person_type:                    z.string(),
  first_name:                     z.string(),
  last_name:                      z.string(),
  name:                           z.string().describe('first_name + last_name'),
  mrn:                            z.string().nullable(),
  donor_code:                     z.string().nullable(),
  date_of_birth:                  z.coerce.date().nullable(),
  blood_type:                     z.string().nullable(),
  phone:                          z.string().nullable(),
  email:                          z.string().nullable(),
  street_address:                 z.string().nullable(),
  city:                           z.string().nullable(),
  zip_code:                       z.string().nullable(),
  facility_id:                    uuidSchema.nullable(),
  unit_id:                        uuidSchema.nullable(),
  bed_id:                         uuidSchema.nullable(),
  child_details:                  z.array(ChildDetailSchema).nullable(),
  emergency_contact_name:         z.string().nullable(),
  emergency_contact_phone:        z.string().nullable(),
  emergency_contact_relationship: z.string().nullable(),
  milk_bank_name:                 z.string().nullable(),
  milk_bank_code:                 z.string().nullable(),
  screening_status:               z.string(),
  screening_date:                 z.preprocess((v) => { if (v == null) return v; return v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v; }, z.iso.date().nullable()),
  screening_expiry_date:          z.preprocess((v) => { if (v == null) return v; return v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v; }, z.iso.date().nullable()),
  first_milk_date:                z.preprocess((v) => { if (v == null) return v; return v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v; }, z.iso.date().nullable()),
  donation_conclusion_date:       z.preprocess((v) => { if (v == null) return v; return v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v; }, z.iso.date().nullable()),
  has_active_exclusion:           z.boolean(),
  is_consent_given:               z.boolean(),
  warning:                        z.string().nullable(),
  pseudonym:                      z.string().nullable(),
  is_active:                      z.boolean(),
  is_active_mother:               z.boolean().optional().nullable().describe('Whether the mother role is active; false when mother role was discharged from a BOTH provider; null for legacy rows predating this field'),
  is_active_donor:                z.boolean().optional().nullable().describe('Whether the donor role is active; false when donor role was discharged from a BOTH provider; null for legacy rows predating this field'),
  notes:                          z.string().nullable(),
  created_at:                     z.coerce.date(),
  updated_at:                     z.coerce.date(),
  // ── Joined: current active exclusion (if any) ─────────────────────────────
  active_exclusion: z.object({
    exclusion_id:         uuidSchema,
    exclusion_type:       z.string(),
    exclusion_start_date: z.preprocess((v) => v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v, z.iso.date()),
    exclusion_end_date:   z.preprocess((v) => { if (v == null) return v; return v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v; }, z.iso.date().nullable()),
    exclusion_reason:     z.string().nullable(),
    is_active:            z.boolean(),
  }).nullable().describe('Most recent active exclusion for this provider, or null'),
  // ── Joined: all exclusions (active + historical) ──────────────────────────
  exclusions: z.array(z.object({
    exclusion_id:         uuidSchema,
    exclusion_type:       z.string(),
    exclusion_start_date: z.preprocess((v) => v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v, z.iso.date()),
    exclusion_end_date:   z.preprocess((v) => { if (v == null) return v; return v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v; }, z.iso.date().nullable()),
    exclusion_reason:     z.string().nullable(),
    is_active:            z.boolean(),
  })).describe('All exclusions for this provider (active and historical), ordered by start date descending'),
  // ── Joined: bed assignment (if any) ──────────────────────────────────────
  bed: z.object({
    bed_id:     uuidSchema,
    bed_number: z.string(),
    bed_type:   z.string(),
    bed_status: z.string(),
  }).nullable().describe('Current bed assignment, or null'),
  // ── Joined: care unit (if any) ───────────────────────────────────────────
  unit: z.object({
    care_unit_id: uuidSchema,
    unit_code:    z.string(),
    unit_name:    z.string(),
    unit_type:    z.string(),
  }).nullable().describe('Care unit the provider is assigned to, or null'),
  // ── Joined: facility (if any) ────────────────────────────────────────────
  facility: z.object({
    facility_id:   uuidSchema,
    facility_code: z.string(),
    facility_name: z.string(),
    facility_type: z.string(),
  }).nullable().describe('Facility the provider is assigned to, or null'),
  // ── Joined: consent records ──────────────────────────────────────────────
  consents: z.array(z.object({
    consent_id:            uuidSchema,
    purpose_id:            uuidSchema,
    purpose_code:          z.string(),
    purpose_name:          z.string(),
    action:                z.string(),
    consent_method:        z.string(),
    consent_form_ref:      z.string().nullable(),
    digital_signature_ref: z.string().nullable(),
    consent_document_ref:  z.string().nullable(),
    consent_version:       z.string().nullable(),
    is_proxy_consent:      z.boolean(),
    guardian_name:         z.string().nullable(),
    guardian_relationship: z.string().nullable(),
    granted_at:            z.coerce.date(),
    expires_at:            z.coerce.date().nullable(),
    withdrawn_at:          z.coerce.date().nullable(),
  })).describe('All consent records for this provider'),
});
export type MilkProviderDetail = z.infer<typeof MilkProviderDetailSchema>;

// ─── Donor-specific schemas (derived from MilkProviderDetailSchema) ─────────

export const DonorProviderDetailSchema = MilkProviderDetailSchema
  .omit({ child_details: true, bed_id: true, bed: true, unit: true, facility: true, consents: true });

const isAtLeast18YearsOld = (dateStr: string) => {
  const today = new Date();
  const birthDate = new Date(dateStr);
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1 >= 18;
  }
  return age >= 18;
};

export const DonorProfileUpdateSchema = z.object({
  phone:                          z.string().max(30, 'Phone must be at most 30 characters').optional().nullable(),
  street_address:                 z.string().max(300, 'Street address must be at most 300 characters').optional().nullable(),
  city:                           z.string().max(100, 'City must be at most 100 characters').optional().nullable(),
  zip_code:                       z.string().max(20, 'Zip code must be at most 20 characters').optional().nullable(),
  date_of_birth:                  z.string().date('Must be a valid date (YYYY-MM-DD)')
    .refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'Date of birth cannot be in the future' })
    .refine((val) => isAtLeast18YearsOld(val), { message: 'mother.validation.donorAgeRequired' })
    .optional().nullable(),
  blood_type:                     bloodTypeSchema.optional().nullable(),
  emergency_contact_name:         z.string().max(200, 'Emergency contact name must be at most 200 characters').optional().nullable(),
  emergency_contact_phone:        z.string().max(30, 'Emergency contact phone must be at most 30 characters').optional().nullable(),
  emergency_contact_relationship: z.string().max(50, 'Emergency contact relationship must be at most 50 characters').optional().nullable(),
}).refine(
  (data) => Object.values(data).some((v) => v !== undefined),
  { message: 'At least one field must be provided for update' },
);

// ─── Profile Lookup Query ───────────────────────────────────────────────────────

export const MilkProviderProfileQuerySchema = z
  .object({
    mrn:  z.string().optional().describe('Exact MRN match'),
    name: z.string().optional().describe('Full or partial name match (first + last)'),
  })
  .refine((d) => d.mrn || d.name, {
    message: 'Provide at least one of: mrn, name',
  });
export type MilkProviderProfileQuery = z.infer<typeof MilkProviderProfileQuerySchema>;

// ─── List Response ────────────────────────────────────────────────────────────

export const MilkProviderListResponseSchema = z.object({
  items: z.array(MilkProviderListItemSchema),
  pagination: z.object({
    currentPage:     z.number(),
    totalPages:      z.number(),
    totalItems:      z.number(),
    itemsPerPage:    z.number(),
    hasNextPage:     z.boolean(),
    hasPreviousPage: z.boolean(),
  }),
});
export type MilkProviderListResponse = z.infer<typeof MilkProviderListResponseSchema>;

// ─── Register (Create) ────────────────────────────────────────────────────────
//
// Single endpoint handles MOTHER, DONOR, and BOTH.
// Required fields vary by personType — enforced via superRefine.
//
// MOTHER  : firstName, lastName, mrn (required)
// DONOR   : firstName, lastName, mrn, donorCode, phone, email, streetAddress,
//           city, zipCode, hospitalId, emergencyContact* (all required)
// BOTH    : union of MOTHER + DONOR required fields

export const InfantLinkSchema = z.object({
  patientId:        uuidSchema.describe('UUID of the linked patient (infant)'),
  relationshipType: z.string().max(20).default('BIOLOGICAL').describe('e.g. BIOLOGICAL, SURROGATE'),
});

export const ExclusionInputSchema = z.object({
  exclusionType:     z.string().max(20).default('OTHER').describe('e.g. MEDICATION, INFECTION, SURGERY, OTHER'),
  exclusionStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format'),
  exclusionEndDate:   z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format').optional(),
  exclusionReason:    z.string().optional(),
});

export const RegisterMilkProviderRequestSchema = z
  .object({
    personType:    z.enum(['MOTHER', 'DONOR', 'BOTH']).describe('Role discriminator'),
    hospitalId:    uuidSchema.describe('Hospital the provider belongs to'),

    // Identity — MRN is auto-generated by the backend
    firstName:    z.string().min(1).max(100),
    lastName:     z.string().min(1).max(100),
    donorCode:    z.string().max(100).optional().describe('Pseudonymous donor code — required for DONOR/BOTH'),

    // Demographics
    dateOfBirth:  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format').refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'Date of birth cannot be in the future' }).optional(),
    bloodType:    z.string().max(10).optional(),

    // Contact
    phone:        z.string().max(30).optional(),
    email:        z.string().email().max(254).optional(),
    streetAddress: z.string().max(300).optional(),
    city:         z.string().max(100).optional(),
    zipCode:      z.string().max(20).optional(),

    // Hospital location
    facilityId:   uuidSchema.optional().describe('Facility the provider is assigned to'),
    unitId:       uuidSchema.optional().describe('Care unit the provider is assigned to'),
    bedId:        uuidSchema.optional().describe('Bed assignment'),

    // Child / NICU
    childDetails: z.array(z.object({
      childDob:    z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format').refine((val) => new Date(val) <= new Date(), { message: 'Child date of birth cannot be in the future' }).describe('Child date of birth'),
      childStatus: ChildStatusEnum.describe('Child status: FULL_TERM | PREMATURE | DECEASED | UNKNOWN'),
    })).optional().describe('Child details array (DONOR/BOTH only) — each entry has childDob and childStatus'),
    infantLinks:  z.array(InfantLinkSchema).optional().describe('Link to NICU infant(s) — MOTHER/BOTH'),

    // Exclusion dates (multiple)
    exclusions:   z.array(ExclusionInputSchema).optional().describe('Exclusion periods — stored in milk_provider_exclusions'),

    // Milk provision
    firstMilkDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format').refine((val) => new Date(val) <= new Date(), { message: 'First milk date cannot be in the future' }).optional().describe('Date this person first provided milk'),

    // Emergency contact
    emergencyContactName:         z.string().max(200).optional(),
    emergencyContactPhone:        z.string().max(30).optional(),
    emergencyContactRelationship: z.string().max(50).optional().describe('e.g. SPOUSE, PARENT, SIBLING'),

    // Donor milk-bank (DONOR/BOTH only)
    milkBankName: z.string().max(200).optional(),
    milkBankCode: z.string().max(50).optional(),

    notes:        z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const req = (field: keyof typeof data, label: string) => {
      const val = data[field];
      if (val === undefined || val === null || val === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} is required for person type ${data.personType}`,
          path: [field],
        });
      }
    };

    if (data.personType === 'DONOR' || data.personType === 'BOTH') {
      req('donorCode', 'Donor Code');

      if (data.dateOfBirth && !isAtLeast18YearsOld(data.dateOfBirth)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'mother.validation.donorAgeRequired',
          path: ['dateOfBirth'],
        });
      }
    }
  });

export type RegisterMilkProviderRequest = z.infer<typeof RegisterMilkProviderRequestSchema>;

export const RegisterMilkProviderResponseSchema = z.object({
  personId:    uuidSchema,
  pseudonymId: uuidSchema.nullable(),
  personType:  z.string(),
  mrn:         z.string().nullable(),
  donorCode:   z.string().nullable(),
  firstName:   z.string(),
  lastName:    z.string(),
  hospitalId:  uuidSchema,
  createdAt:   z.coerce.date(),
});
export type RegisterMilkProviderResponse = z.infer<typeof RegisterMilkProviderResponseSchema>;

// ─── Register Mother (snake_case — used by tms-api-patient legacy routes) ────

export const RegisterRequestSchema = z.object({
  hospital_id:                   uuidSchema,
  person_type:                   PersonTypeSchema,
  first_name:                    z.string().min(1).max(100),
  last_name:                     z.string().min(1).max(100),
  mrn:                           z.string().min(1).max(50).optional().describe('Hospital MRN for the mother (optional)'),
  date_of_birth:                 z.string().date().refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'Date of birth cannot be in the future' }).optional().describe('Date of birth (YYYY-MM-DD)'),
  blood_type:                    z.string().max(10).optional().nullable(),
  phone:                         z.string().max(30).optional().nullable(),
  email:                         z.string().email().max(255).optional().nullable(),
  street_address:                z.string().max(300).optional().nullable(),
  city:                          z.string().max(100).optional().nullable(),
  zip_code:                      z.string().max(20).optional().nullable(),
  facility_id:                   uuidSchema.optional().nullable().describe('FK → ent_facilities.facility_id'),
  unit_id:                       uuidSchema.optional().nullable().describe('FK → ent_care_units.care_unit_id'),
  bed_id:                        uuidSchema.optional().nullable().describe('FK → ent_beds.bed_id'),
  child_details:                 z.array(ChildDetailSchema).optional().nullable().describe('Child details array (DONOR/BOTH only) — each entry has child_dob and child_status (ALIVE/DEAD)'),
  donor_code:                    z.string().max(50).optional().nullable().describe('Pseudonymous donor code — required for DONOR/BOTH'),
  screening_status:              z.string().optional().describe('Screening status — auto-resolved if omitted'),
  screening_date:                z.string().date().refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'Screening date cannot be in the future' }).optional().nullable().describe('Date of screening (YYYY-MM-DD)'),
  screening_expiry_date:         z.string().date().optional().nullable().describe('Screening expiry date (YYYY-MM-DD)'),
  milk_bank_name:                z.string().max(200).optional().nullable().describe('Milk bank name (DONOR/BOTH only)'),
  milk_bank_code:                z.string().max(50).optional().nullable().describe('Milk bank code (DONOR/BOTH only)'),
  first_milk_date:               z.string().date().refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'First milk date cannot be in the future' }).optional().nullable().describe('Date of first milk expression (YYYY-MM-DD)'),
  donation_conclusion_date:      z.string().date().refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'Conclusion of Donation Date cannot be in the future' }).optional().nullable().describe('Date the donor concluded milk donation (YYYY-MM-DD)'),
  warning:                         z.string().optional().nullable().describe('Free-text warning note'),
  emergency_contact_name:        z.string().max(200).optional().nullable(),
  emergency_contact_phone:       z.string().max(30).optional().nullable(),
  emergency_contact_relationship: z.string().max(50).optional().nullable(),
  notes:                         z.string().optional().nullable(),
  is_consent_given:              z.boolean().optional().default(false).describe('DONOR/BOTH only — when true, auto-creates a DONOR_MILK_USAGE clinical_consent_records row. Not applicable for MOTHER.'),
  staff_id:                      uuidSchema.optional().describe('UUID of the staff member — required when is_consent_given is true'),
  exclusions:                    z.array(InlineExclusionSchema).optional().describe('Initial exclusion records — each creates a milk_provider_exclusions row'),
  consents:                      z.array(InlineConsentSchema).optional().describe('Consent records to create alongside this registration — each creates a clinical_consent_records row.'),
}).superRefine((data, ctx) => {
  if (data.person_type === 'MOTHER' && data.is_consent_given === true) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['is_consent_given'],
      message: 'Consent is not applicable for MOTHER — only for DONOR or BOTH.',
    });
  }
  if (data.first_milk_date && data.donation_conclusion_date && data.donation_conclusion_date < data.first_milk_date) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['donation_conclusion_date'],
      message: 'Conclusion of Donation Date cannot be before Milk Start Date',
    });
  }
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

// ─── Register response ────────────────────────────────────────────────────────

export const MilkProviderResponseSchema = z.object({
  person_id:                     uuidSchema,
  hospital_id:                   uuidSchema,
  person_type:                   z.string(),
  mrn:                           z.string().nullable(),
  donor_code:                    z.string().nullable(),
  first_name:                    z.string(),
  last_name:                     z.string(),
  date_of_birth:                 z.date().nullable(),
  blood_type:                    z.string().nullable(),
  phone:                         z.string().nullable(),
  email:                         z.string().nullable(),
  street_address:                z.string().nullable(),
  city:                          z.string().nullable(),
  zip_code:                      z.string().nullable(),
  facility_id:                   uuidSchema.nullable(),
  unit_id:                       uuidSchema.nullable(),
  bed_id:                        uuidSchema.nullable(),
  child_details:                 z.array(ChildDetailSchema).nullable(),
  emergency_contact_name:        z.string().nullable(),
  emergency_contact_phone:       z.string().nullable(),
  emergency_contact_relationship: z.string().nullable(),
  milk_bank_name:                z.string().nullable(),
  milk_bank_code:                z.string().nullable(),
  screening_status:              z.string(),
  screening_date:                z.date().nullable(),
  screening_expiry_date:         z.date().nullable(),
  rejection_reason:              z.string().nullable(),
  approved_by:                   uuidSchema.nullable(),
  approved_at:                   z.date().nullable(),
  first_milk_date:               z.date().nullable(),
  donation_conclusion_date:      z.date().nullable(),
  has_active_exclusion:          z.boolean(),
  is_consent_given:              z.boolean(),
  warning:                       z.string().nullable(),
  pseudonym_id:                  uuidSchema.nullable(),
  is_active:                     z.boolean(),
  is_active_mother:              z.boolean(),
  is_active_donor:               z.boolean(),
  notes:                         z.string().nullable(),
  created_at:                    z.date(),
  updated_at:                    z.date(),
  // ── Inline consents (present only when consents were submitted at registration) ──
  consents:                      z.array(z.object({
    consent_id:            uuidSchema,
    person_id:             uuidSchema,
    hospital_id:           uuidSchema,
    purpose_id:            uuidSchema,
    action:                z.string(),
    consent_method:        z.string(),
    consent_form_ref:      z.string().nullable(),
    digital_signature_ref: z.string().nullable(),
    consent_document_ref:  z.string().nullable(),
    consent_version:       z.string().nullable(),
    is_proxy_consent:      z.boolean(),
    guardian_name:         z.string().nullable(),
    guardian_relationship: z.string().nullable(),
    recorded_by:           uuidSchema,
    granted_at:            z.string().datetime({ offset: true, local: true }),
    expires_at:            z.string().datetime({ offset: true, local: true }).nullable(),
    withdrawn_at:          z.string().datetime({ offset: true, local: true }).nullable(),
    notes:                 z.string().nullable(),
    created_at:            z.string().datetime({ offset: true, local: true }),
  })).optional(),
  // ── Inline exclusions (present only when exclusions were submitted) ──────────
  exclusions: z.array(z.object({
    exclusion_id:         uuidSchema,
    person_id:            uuidSchema,
    hospital_id:          uuidSchema,
    exclusion_type:       z.string(),
    exclusion_start_date: z.date(),
    exclusion_end_date:   z.date().nullable(),
    exclusion_reason:     z.string().nullable(),
    is_active:            z.boolean(),
    recorded_by:          uuidSchema,
    notes:                z.string().nullable(),
    created_at:           z.date(),
  })).optional(),
});
export type MilkProviderResponse = z.infer<typeof MilkProviderResponseSchema>;

// ─── Exclusion response (re-used inline and standalone) ───────────────────────

export const MilkProviderExclusionResponseSchema = z.object({
  exclusion_id:         uuidSchema,
  person_id:            uuidSchema,
  hospital_id:          uuidSchema,
  exclusion_type:       z.string(),
  exclusion_start_date: z.date(),
  exclusion_end_date:   z.date().nullable(),
  exclusion_reason:     z.string().nullable(),
  is_active:            z.boolean(),
  recorded_by:          uuidSchema,
  notes:                z.string().nullable(),
  created_at:           z.date(),
});
export type MilkProviderExclusionResponse = z.infer<typeof MilkProviderExclusionResponseSchema>;

// ─── Consent record ───────────────────────────────────────────────────────────

export const AddMilkProviderConsentRequestSchema = z.object({
  hospital_id:           uuidSchema,
  purpose_id:            uuidSchema.describe('UUID of the consent_purposes row'),
  action:                z.enum(['GRANTED', 'WITHDRAWN', 'REFUSED']).describe('Consent action'),
  consent_method:        z.enum(['WRITTEN_FORM', 'VERBAL', 'ELECTRONIC']).describe('How consent was captured'),
  recorded_by:           uuidSchema.describe('UUID of the staff member recording consent'),
  digital_signature_ref: z.string().max(500).optional().nullable().describe('Reference to a digital signature document'),
  consent_document_ref:  z.string().max(500).optional().nullable().describe('Reference to the physical consent form'),
  consent_version:       z.string().max(20).optional().nullable(),
  is_proxy_consent:      z.boolean().optional().default(false),
  guardian_name:         z.string().max(200).optional().nullable(),
  guardian_relationship: z.string().max(30).optional().nullable(),
  consent_form_ref:      z.string().max(200).optional().nullable().describe('Reference to the physical consent form'),
  granted_at:            z.string().datetime({ offset: true, local: true }).optional().nullable().describe('ISO 8601 timestamp — defaults to now()'),
  expires_at:            z.string().datetime({ offset: true, local: true }).optional().nullable().describe('Optional expiry timestamp'),
  notes:                 z.string().optional().nullable(),
});
export type AddMilkProviderConsentRequest = z.infer<typeof AddMilkProviderConsentRequestSchema>;

export const MilkProviderConsentResponseSchema = z.object({
  consent_id:            uuidSchema,
  person_id:             uuidSchema,
  hospital_id:           uuidSchema,
  purpose_id:            uuidSchema,
  action:                z.string(),
  consent_method:        z.string(),
  consent_form_ref:      z.string().nullable(),
  digital_signature_ref: z.string().nullable(),
  consent_document_ref:  z.string().nullable(),
  consent_version:       z.string().nullable(),
  is_proxy_consent:      z.boolean(),
  guardian_name:         z.string().nullable(),
  guardian_relationship: z.string().nullable(),
  recorded_by:           uuidSchema,
  granted_at:            z.string().datetime({ offset: true, local: true }),
  expires_at:            z.string().datetime({ offset: true, local: true }).nullable(),
  withdrawn_at:          z.string().datetime({ offset: true, local: true }).nullable(),
  notes:                 z.string().nullable(),
  created_at:            z.string().datetime({ offset: true, local: true }),
});
export type MilkProviderConsentResponse = z.infer<typeof MilkProviderConsentResponseSchema>;

// ─── Milk provider summary (GET /mothers) ────────────────────────────────────

/**
 * Slim shape returned by GET /milkProviders/mothers.
 * Only MOTHER and BOTH person_type rows are returned (is_active = true).
 */
export const MilkProviderSummarySchema = z.object({
  person_id: uuidSchema,
  name:      z.string().describe('Full name: "first_name last_name"'),
});
export type MilkProviderSummary = z.infer<typeof MilkProviderSummarySchema>;

// ─── Update milk provider (PATCH) ────────────────────────────────────────────

/**
 * All demographic and operational fields are optional — only the supplied
 * fields are written.  person_type cannot be changed after registration.
 * hospital_id is required so the service can validate hospital ownership.
 */

/**
 * Updatable fields on a clinical_consent_records row.
 * consent_id is required to identify which record to update.
 * All other fields are optional — only supplied fields are written.
 */
export const UpdateConsentSchema = z.object({
  consent_id:            uuidSchema.describe('UUID of the consent record to update'),
  action:                z.enum(['GRANTED', 'WITHDRAWN', 'REFUSED']).optional().describe('Consent action'),
  consent_method:        z.enum(['WRITTEN_FORM', 'VERBAL', 'ELECTRONIC']).optional().describe('How consent was captured'),
  recorded_by:           uuidSchema.optional().describe('UUID of the staff member recording consent'),
  digital_signature_ref: z.string().max(500).optional().nullable(),
  consent_document_ref:  z.string().max(500).optional().nullable(),
  consent_version:       z.string().max(20).optional().nullable(),
  is_proxy_consent:      z.boolean().optional(),
  guardian_name:         z.string().max(200).optional().nullable(),
  guardian_relationship: z.string().max(30).optional().nullable(),
  consent_form_ref:      z.string().max(200).optional().nullable(),
  granted_at:            z.string().datetime({ offset: true, local: true }).optional().nullable().describe('ISO 8601 timestamp'),
  expires_at:            z.string().datetime({ offset: true, local: true }).optional().nullable(),
  withdrawn_at:          z.string().datetime({ offset: true, local: true }).optional().nullable().describe('Set when action is WITHDRAWN'),
  notes:                 z.string().optional().nullable(),
});
export type UpdateConsent = z.infer<typeof UpdateConsentSchema>;

export const UpdateMilkProviderRequestSchema = z.object({
  hospital_id:                    uuidSchema,
  person_type:                    PersonTypeSchema.optional().describe('Change person type — upgrading to DONOR/BOTH will auto-generate a donor_code if one is not already set'),
  first_name:                     z.string().min(1).max(100).optional(),
  last_name:                      z.string().min(1).max(100).optional(),
  mrn:                            z.string().min(1).max(50).optional().nullable(),
  donor_code:                     z.string().min(1).max(100).optional().nullable(),
  date_of_birth:                  z.string().date().refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'Date of birth cannot be in the future' }).optional().nullable(),
  blood_type:                     z.string().max(10).optional().nullable(),
  phone:                          z.string().max(30).optional().nullable(),
  email:                          z.string().email().max(254).optional().nullable(),
  street_address:                 z.string().max(300).optional().nullable(),
  city:                           z.string().max(100).optional().nullable(),
  zip_code:                       z.string().max(20).optional().nullable(),
  facility_id:                    uuidSchema.optional().nullable().describe('FK → ent_facilities.facility_id'),
  unit_id:                        uuidSchema.optional().nullable().describe('FK → ent_care_units.care_unit_id'),
  bed_id:                         uuidSchema.optional().nullable().describe('FK → ent_beds.bed_id'),
  child_details:                  z.array(ChildDetailSchema).optional().nullable().describe('Child details array (DONOR/BOTH only) — each entry has child_dob and child_status (ALIVE/DEAD)'),
  milk_bank_name:                 z.string().max(200).optional().nullable(),
  milk_bank_code:                 z.string().max(50).optional().nullable(),
  screening_date:                 z.string().date().optional().nullable(),
  screening_expiry_date:          z.string().date().optional().nullable(),
  first_milk_date:                z.string().date().optional().nullable(),
  donation_conclusion_date:       z.string().date().refine((val) => val <= new Date().toISOString().slice(0, 10), { message: 'Conclusion of Donation Date cannot be in the future' }).optional().nullable(),
  emergency_contact_name:         z.string().max(200).optional().nullable(),
  emergency_contact_phone:        z.string().max(30).optional().nullable(),
  emergency_contact_relationship: z.string().max(50).optional().nullable(),
  warning:                          z.string().optional().nullable().describe('Free-text warning note'),
  is_active:                      z.boolean().optional(),
  notes:                          z.string().optional().nullable(),
  is_consent_given:               z.boolean().optional().describe('DONOR/BOTH only — when true, auto-creates a DONOR_MILK_USAGE consent record. When false, withdraws existing consent. Not applicable for MOTHER.'),
  staff_id:                       uuidSchema.optional().describe('UUID of the staff member — required when is_consent_given is true'),
  exclusions:                     z.array(InlineExclusionSchema).optional().describe('Exclusion records to create alongside this provider update'),
  consent:                        UpdateConsentSchema.optional().describe('Consent record to update alongside this provider update'),
  // ── Re-admit fields (readmit=true activates the role without changing person_type) ──
  readmit:                        z.boolean().optional().default(false).describe('When true, re-activates the specified role rather than performing a demographic update'),
  readmit_role:                   z.enum(['MOTHER', 'DONOR', 'BOTH']).optional().describe('Which role to re-activate. Required when readmit=true.'),
  readmit_reason:                 z.string().max(500).optional().nullable(),
  readmit_date:                   z.string().datetime({ offset: true, local: true }).optional(),
}).superRefine((data, ctx) => {
  const isDonor = data.person_type === 'DONOR' || data.person_type === 'BOTH';
  if (isDonor && data.date_of_birth && !isAtLeast18YearsOld(data.date_of_birth)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'mother.validation.donorAgeRequired',
      path: ['date_of_birth'],
    });
  }
  if (data.first_milk_date && data.donation_conclusion_date && data.donation_conclusion_date < data.first_milk_date) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['donation_conclusion_date'],
      message: 'Conclusion of Donation Date cannot be before Milk Start Date',
    });
  }
});
export type UpdateMilkProviderRequest = z.infer<typeof UpdateMilkProviderRequestSchema>;

// ─── Convert Mother to Donor ─────────────────────────────────────────────────

/**
 * Request body for POST /milkProviders/convertToDonor.
 *
 * Converts a MOTHER to BOTH (mother + donor).  The person must currently have
 * person_type = 'MOTHER'.  Donors (DONOR) and already-converted providers
 * (BOTH) are rejected with 400.
 *
 * This is a simple checkbox action — the backend auto-generates the donor_code
 * and sets screening_status to PENDING.  Both person_id and hospital_id are required.
 */
export const ConvertMotherToDonorRequestSchema = z.object({
  person_id:                      uuidSchema.describe('UUID of the milk provider (must be a MOTHER) to convert'),
  hospital_id:                    uuidSchema,
  staff_id:                       uuidSchema.optional().describe('UUID of the staff member performing the conversion (for audit trail)'),
});
export type ConvertMotherToDonorRequest = z.infer<typeof ConvertMotherToDonorRequestSchema>;

// ─── Milk Provider Health Records ────────────────────────────────────────────

export const AddHealthRecordRequestSchema = z.object({
  hospital_id:                  uuidSchema,
  record_type:                  z.enum(['SEROLOGY', 'OBSTETRIC_HISTORY', 'HEALTH_HISTORY', 'LIFESTYLE', 'MEDICATION', 'OTHER']).describe('Category of health record'),
  test_type:                    z.string().max(100).optional().nullable().describe('e.g. CMV_IgG, HIV_Ab, HBsAg'),
  test_date:                    z.string().date().optional().nullable().describe('Date test was performed (YYYY-MM-DD)'),
  result:                       z.enum(['NEGATIVE', 'POSITIVE', 'INDETERMINATE', 'PENDING', 'N_A']).optional().nullable(),
  result_details:               z.string().optional().nullable(),
  lab_reference:                z.string().max(100).optional().nullable(),
  is_recurring_test:            z.boolean().optional().default(false),
  next_test_date:               z.string().date().optional().nullable().describe('Next scheduled test date (YYYY-MM-DD)'),
  gravida:                      z.number().int().min(0).max(99).optional().nullable().describe('Number of pregnancies'),
  para:                         z.number().int().min(0).max(99).optional().nullable().describe('Number of births ≥20 weeks'),
  condition_flags:              z.record(z.string(), z.boolean()).optional().nullable().describe('Key-value flags, e.g. {"HIV": false, "HBV": false}'),
  past_medical_history:         z.string().optional().nullable(),
  current_medications_summary:  z.string().optional().nullable(),
  allergies:                    z.string().optional().nullable(),
  history_of_mastitis:          z.boolean().optional().default(false),
  nicotine_use:                 z.boolean().optional().nullable(),
  alcohol_use:                  z.enum(['NONE', 'OCCASIONAL', 'MODERATE', 'REGULAR', 'UNKNOWN']).optional().nullable(),
  diet_type:                    z.enum(['OMNIVORE', 'VEGETARIAN', 'VEGAN', 'HALAL', 'KOSHER', 'RESTRICTED', 'OTHER']).optional().nullable(),
  medications_detail:           z.string().optional().nullable(),
  performed_by:                 uuidSchema.optional().nullable(),
});
export type AddHealthRecordRequest = z.infer<typeof AddHealthRecordRequestSchema>;

// ─── Donor Dashboard ──────────────────────────────────────────────────────────

// ─── Donor Monthly Impact ─────────────────────────────────────────────────────

export const DonorMonthlyImpactItemSchema = z.object({
  month:                z.string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be in YYYY-MM format with a valid month (01–12)')
    .describe('Year-month in YYYY-MM format, e.g. "2025-01"'),
  total_volume_used_ml: z.number().nonnegative().describe('Sum of quantity_used (ml) for this month'),
  babies_helped:        z.number().int().nonnegative().describe('Distinct patients fed this month'),
  splits_completed:     z.number().int().nonnegative().describe('Count of completed feed prep splits attributed to this donor in this month'),
});
export type DonorMonthlyImpactItem = z.infer<typeof DonorMonthlyImpactItemSchema>;

export const DonorMonthlyImpactSchema = z.object({
  person_id: uuidSchema,
  months:    z.array(DonorMonthlyImpactItemSchema),
});
export type DonorMonthlyImpact = z.infer<typeof DonorMonthlyImpactSchema>;

// ─── Donor Dashboard ──────────────────────────────────────────────────────────

export const DonorDashboardSchema = z.object({
  person_id:            uuidSchema,
  count:                z.number().int().nonnegative().describe('Total number of milk receivable records for this donor'),
  total_volume_ml:      z.number().nonnegative().describe('Sum of volume_ml across all milk receivable records for this donor'),
  total_volume_used_ml: z.number().nonnegative().describe('Sum of quantity_used from feed_prep_items linked via milk_inventory → milk_receivables for this donor'),
  babies_helped:        z.number().int().nonnegative().describe('Count of distinct patients with feed_orders linked to this donor via feed_preparations → feed_prep_items → milk_inventory → milk_receivables'),
  active_days:          z.number().int().nonnegative().nullable().describe('Number of days since milk_providers.approved_at. Null if the donor has not been approved yet.'),
});
export type DonorDashboard = z.infer<typeof DonorDashboardSchema>;

export const HealthRecordResponseSchema = z.object({
  health_record_id:             uuidSchema,
  person_id:                    uuidSchema,
  hospital_id:                  uuidSchema,
  record_type:                  z.string(),
  test_type:                    z.string().nullable(),
  test_date:                    z.date().nullable(),
  result:                       z.string().nullable(),
  result_details:               z.string().nullable(),
  lab_reference:                z.string().nullable(),
  is_recurring_test:            z.boolean(),
  next_test_date:               z.date().nullable(),
  gravida:                      z.number().nullable(),
  para:                         z.number().nullable(),
  condition_flags:              z.record(z.string(), z.boolean()).nullable(),
  past_medical_history:         z.string().nullable(),
  current_medications_summary:  z.string().nullable(),
  allergies:                    z.string().nullable(),
  history_of_mastitis:          z.boolean(),
  nicotine_use:                 z.boolean().nullable(),
  alcohol_use:                  z.string().nullable(),
  diet_type:                    z.string().nullable(),
  medications_detail:           z.string().nullable(),
  performed_by:                 uuidSchema.nullable(),
  created_at:                   z.date(),
});
export type HealthRecordResponse = z.infer<typeof HealthRecordResponseSchema>;

// ─── Health Record Attachments ───────────────────────────────────────────────

export const AddHealthRecordAttachmentRequestSchema = z.object({
  hospital_id:         uuidSchema,
  subject_person_type: z.enum(['MOTHER', 'DONOR', 'BOTH']).describe('Person type of the provider this record belongs to'),
  attachment_type:     z.enum(['LAB_RESULT', 'CONSENT_FORM', 'SCREENING_REPORT', 'OTHER']).optional().default('OTHER'),
  reason:              z.string().optional().nullable(),
  file_name:           z.string().min(1).max(255),
  file_path:           z.string().min(1).max(500).describe('Storage path / S3 key'),
  file_size_bytes:     z.number().int().positive().optional().nullable(),
  mime_type:           z.string().max(100).optional().nullable(),
  description:         z.string().optional().nullable(),
  uploaded_by:         uuidSchema,
});
export type AddHealthRecordAttachmentRequest = z.infer<typeof AddHealthRecordAttachmentRequestSchema>;

export const HealthRecordAttachmentResponseSchema = z.object({
  attachment_id:       uuidSchema,
  health_record_id:    uuidSchema,
  hospital_id:         uuidSchema,
  subject_person_type: z.string(),
  attachment_type:     z.string(),
  reason:              z.string().nullable(),
  file_name:           z.string(),
  file_path:           z.string(),
  file_size_bytes:     z.number().nullable(),
  mime_type:           z.string().nullable(),
  description:         z.string().nullable(),
  uploaded_by:         uuidSchema,
  uploaded_at:         z.date(),
  is_active:           z.boolean(),
  created_at:          z.date(),
});
export type HealthRecordAttachmentResponse = z.infer<typeof HealthRecordAttachmentResponseSchema>;

// ─── Milk Collection Log ──────────────────────────────────────────────────────

export const LogMilkCollectionRequestSchema = z.object({
  hospital_id:                 uuidSchema,
  provider_id:                 uuidSchema.describe('FK → milk_providers.person_id'),
  patient_id:                  uuidSchema.optional().nullable().describe('Intended recipient patient, if known'),
  encounter_id:                uuidSchema.optional().nullable(),
  collected_at:                z.string().datetime({ offset: true, local: true }).describe('ISO 8601 timestamp of collection'),
  volume_ml:                   z.number().positive().describe('Volume collected in millilitres'),
  collection_method:           z.enum(['PUMPED_ELECTRIC', 'PUMPED_MANUAL', 'HAND_EXPRESSED', 'BROUGHT_FROM_HOME', 'DONOR_DELIVERY', 'OTHER']).optional().nullable(),
  storage_condition_on_receipt: z.enum(['FRESH', 'REFRIGERATED', 'FROZEN']).optional().default('FRESH'),
  intended_use:                z.enum(['DIRECT_FEED', 'FORTIFICATION', 'DONOR_POOL', 'UNSPECIFIED']).optional().default('UNSPECIFIED'),
  batch_id:                    uuidSchema.optional().nullable().describe('FK → milk_batches.batch_id, if already batched'),
  status:                      z.enum(['LOGGED', 'SUBMITTED', 'PROCESSED', 'DISCARDED']).optional().default('LOGGED'),
  logged_by:                   uuidSchema,
  notes:                       z.string().optional().nullable(),
});
export type LogMilkCollectionRequest = z.infer<typeof LogMilkCollectionRequestSchema>;

export const MilkCollectionLogResponseSchema = z.object({
  log_id:                       uuidSchema,
  hospital_id:                  uuidSchema,
  provider_id:                  uuidSchema,
  patient_id:                   uuidSchema.nullable(),
  encounter_id:                 uuidSchema.nullable(),
  collected_at:                 z.coerce.date(),
  volume_ml:                    z.number(),
  collection_method:            z.string().nullable(),
  storage_condition_on_receipt: z.string(),
  intended_use:                 z.string(),
  batch_id:                     uuidSchema.nullable(),
  status:                       z.string(),
  logged_by:                    uuidSchema,
  notes:                        z.string().nullable(),
  created_at:                   z.date(),
  updated_at:                   z.date(),
});
export type MilkCollectionLogResponse = z.infer<typeof MilkCollectionLogResponseSchema>;

// ─── Patient Donor Preferences ────────────────────────────────────────────────

export const SetDonorPreferenceRequestSchema = z.object({
  hospital_id:    uuidSchema,
  patient_id:     uuidSchema,
  encounter_id:   uuidSchema.optional().nullable(),
  donor_id:       uuidSchema.describe('FK → milk_providers.person_id (DONOR type)'),
  priority_order: z.number().int().min(1).optional().default(1).describe('1 = highest priority'),
  reason:         z.string().optional().nullable(),
  is_active:      z.boolean().optional().default(true),
  set_by:         z.string().min(1).max(200).optional(),
  effective_from: z.string().datetime({ offset: true, local: true }).optional().nullable(),
  effective_to:   z.string().datetime({ offset: true, local: true }).optional().nullable(),
});
export type SetDonorPreferenceRequest = z.infer<typeof SetDonorPreferenceRequestSchema>;

export const DonorPreferenceResponseSchema = z.object({
  preference_id:  uuidSchema,
  hospital_id:    uuidSchema,
  patient_id:     uuidSchema,
  encounter_id:   uuidSchema.nullable(),
  donor_id:       uuidSchema,
  priority_order: z.number(),
  reason:         z.string().nullable(),
  is_active:      z.boolean(),
  set_by:         z.string().min(1).max(200).optional(),
  effective_from: z.date(),
  effective_to:   z.date().nullable(),
  created_at:     z.date(),
  updated_at:     z.date(),
});
export type DonorPreferenceResponse = z.infer<typeof DonorPreferenceResponseSchema>;

// ─── Patient Medical History ─────────────────────────────────────────────────

export const AddPatientMedicalHistoryRequestSchema = z.object({
  hospital_id:   uuidSchema,
  patient_id:    uuidSchema,
  encounter_id:  uuidSchema.optional().nullable(),
  category:      z.enum(['BIRTH_HISTORY', 'SURGICAL_HISTORY', 'CHRONIC_CONDITIONS', 'ALLERGIES', 'MEDICATIONS', 'FAMILY_HISTORY', 'OTHER']).optional().default('OTHER'),
  narrative:     z.string().min(1).describe('Free-text clinical narrative'),
  recorded_by:   uuidSchema,
  recorded_at:   z.string().datetime({ offset: true, local: true }).optional().nullable(),
  is_active:     z.boolean().optional().default(true),
});
export type AddPatientMedicalHistoryRequest = z.infer<typeof AddPatientMedicalHistoryRequestSchema>;

export const PatientMedicalHistoryResponseSchema = z.object({
  history_id:   uuidSchema,
  patient_id:   uuidSchema,
  encounter_id: uuidSchema.nullable(),
  hospital_id:  uuidSchema,
  category:     z.string(),
  narrative:    z.string(),
  recorded_by:  uuidSchema,
  recorded_at:  z.date(),
  is_active:    z.boolean(),
  created_at:   z.date(),
  updated_at:   z.date(),
});
export type PatientMedicalHistoryResponse = z.infer<typeof PatientMedicalHistoryResponseSchema>;

// ─── Discharge milk provider ──────────────────────────────────────────────────

/**
 * POST /api/v1/milkProviders/:person_id/discharge
 *
 * Supports three scenarios:
 *  - person_type = MOTHER  : full discharge (is_active → false)
 *  - person_type = DONOR   : full discharge (is_active → false)
 *  - person_type = BOTH    : discharge_role required.
 *    - discharge_role = BOTH   → full discharge, is_active=false, person_type unchanged
 *    - discharge_role = MOTHER → mother discharged, person_type→DONOR, is_active=true
 *    - discharge_role = DONOR  → donor  discharged, person_type→MOTHER, is_active=true
 */
export const DischargeMilkProviderRequestSchema = z.object({
  hospital_id:      uuidSchema,
  discharge_role:   z.enum(['MOTHER', 'DONOR', 'BOTH']).optional()
    .describe('Role to discharge. Required when person_type is BOTH. Inferred from person_type for single-role providers.'),
  discharge_reason: z.string().max(500).optional().nullable()
    .describe('Free-text clinical reason for discharge'),
  discharge_date:   z.string().datetime({ offset: true, local: true }).optional()
    .describe('Discharge date/time (ISO 8601). Defaults to current time if omitted'),
  disable_user:     z.boolean().default(false)
    .describe('DONOR role only — if true, disables the Cognito donor account. Silently ignored for MOTHER discharge'),
});
export type DischargeMilkProviderRequest = z.infer<typeof DischargeMilkProviderRequestSchema>;

export const DischargeMilkProviderResponseSchema = z.object({
  person_id:                uuidSchema,
  hospital_id:              uuidSchema,
  person_type:              z.string()
    .describe('Original person_type — never mutated by discharge'),
  effective_discharge_role: z.enum(['MOTHER', 'DONOR', 'BOTH'])
    .describe('Which role(s) were discharged in this call'),
  is_active:                z.boolean()
    .describe('false when both roles are inactive; true when at least one role remains active'),
  is_active_mother:         z.boolean()
    .describe('false when the MOTHER role has been discharged'),
  is_active_donor:          z.boolean()
    .describe('false when the DONOR role has been discharged'),
  discharged_at:            z.string().datetime()
    .describe('ISO timestamp when discharge was processed'),
  discharge_reason:         z.string().nullable().optional(),
  cognito_user_disabled:    z.boolean()
    .describe('true if the Cognito donor account was successfully disabled; always false for MOTHER discharge'),
  related_records_closed: z.object({
    milk_inventory_sent_home: z.array(z.object({
      receivable_id:                uuidSchema,
      receipt_number:               z.string(),
      volume_ml:                    z.number(),
      collected_at:                 z.string().datetime(),
      received_at:                  z.string().datetime(),
      expiry_at:                    z.string().datetime().nullable(),
      milk_type:                    z.string().nullable(),
      storage_condition_on_receipt: z.string(),
    })),
    consent_records_withdrawn:             z.number().int(),
    active_exclusions_closed:              z.number().int(),
  }),
});
export type DischargeMilkProviderResponse = z.infer<typeof DischargeMilkProviderResponseSchema>;

// ─── Re-admit mother ──────────────────────────────────────────────────────────────────────────

export const ReAdmitMotherRequestSchema = z.object({
  hospital_id:    uuidSchema,
  facility_id:    uuidSchema.optional(),
  unit_id:        uuidSchema.optional(),
  bed_id:         uuidSchema.optional().nullable(),
  admit_date:     z.string().datetime({ offset: true, local: true }).optional()
    .describe('Defaults to current time if omitted'),
  readmit_reason: z.string().max(500).optional().nullable(),
});
export type ReAdmitMotherRequest = z.infer<typeof ReAdmitMotherRequestSchema>;

export const ReAdmitMotherResponseSchema = z.object({
  person_id:            uuidSchema,
  hospital_id:          uuidSchema,
  previous_person_type: z.string()
    .describe('person_type before re-admission'),
  person_type:          z.enum(['MOTHER', 'BOTH'])
    .describe("'BOTH' when the donor sub-role was still active at readmit time (DONOR→BOTH); otherwise 'MOTHER'"),
  is_active:            z.literal(true),
  readmitted_at:        z.string().datetime(),
  readmit_reason:       z.string().nullable().optional(),
  records_restored: z.object({
    bed_assigned: z.boolean(),
  }),
});
export type ReAdmitMotherResponse = z.infer<typeof ReAdmitMotherResponseSchema>;

// ─── Re-admit donor ───────────────────────────────────────────────────────────────────────────
// Mirrors ReAdmitMother but for the DONOR role. Also accepts:
//  - editable demographics (the "prefilled but editable" Demographics step of the readmit modal)
//  - an optional consent re-capture flag (Consent step, blank by default)
//  - Lifestyle + Medical History health-screening fields (Health Screening step, blank by
//    default) -- persisted as a brand-new HEALTH_HISTORY/LIFESTYLE record; the prior record(s)
//    are archived (soft-deleted), not overwritten, so they remain available as "Past" history.
// screening_status is always reset to PENDING (re-approval required) and person_type is never
// forced back to a single role -- only the DONOR role flag is activated.

export const ReAdmitDonorRequestSchema = z.object({
  hospital_id:    uuidSchema,
  facility_id:    uuidSchema.optional().nullable(),
  unit_id:        uuidSchema.optional().nullable(),
  bed_id:         uuidSchema.optional().nullable(),
  admit_date:     z.string().datetime({ offset: true, local: true }).optional()
    .describe('Defaults to current time if omitted'),
  readmit_reason: z.string().max(500).optional().nullable(),

  // -- Demographics (prefilled from the current record, editable) --
  phone:                   z.string().max(20).optional().nullable(),
  email:                   z.string().email().max(255).optional().nullable(),
  street_address:          z.string().max(255).optional().nullable(),
  city:                    z.string().max(100).optional().nullable(),
  zip_code:                z.string().max(20).optional().nullable(),
  emergency_contact_name:  z.string().max(150).optional().nullable(),
  emergency_contact_phone: z.string().max(20).optional().nullable(),

  // -- Consent (blank by default, re-captured) --
  is_consent_given: z.boolean().optional(),

  // -- Health Screening: Lifestyle + Medical History (blank by default, re-captured) --
  medical_conditions:   z.array(z.string().max(100)).optional(),
  past_medical_history: z.string().max(2000).optional().nullable(),
  current_medications:  z.string().max(2000).optional().nullable(),
  allergies:            z.string().max(500).optional().nullable(),
  history_of_mastitis:  z.boolean().optional(),
  nicotine_use:         z.boolean().nullable().optional(),
  alcohol_use:          MilkProviderAlcoholUse.optional().nullable(),
  diet_type:            MilkProviderDietType.optional().nullable(),
  lifestyle_warning:    z.string().max(500).optional().nullable(),
});
export type ReAdmitDonorRequest = z.infer<typeof ReAdmitDonorRequestSchema>;

export const ReAdmitDonorResponseSchema = z.object({
  person_id:             uuidSchema,
  hospital_id:           uuidSchema,
  previous_person_type:  z.string()
    .describe('person_type before re-admission'),
  person_type:           z.enum(['DONOR', 'BOTH'])
    .describe("'BOTH' when the mother sub-role was still active at readmit time; otherwise 'DONOR'"),
  is_active:             z.literal(true),
  screening_status:      z.literal('PENDING')
    .describe('Donor approval is always reset to Pending on readmit -- re-processing required'),
  donor_code:            z.string(),
  readmitted_at:         z.string().datetime(),
  readmit_reason:        z.string().nullable().optional(),
  donor_portal_access_enabled: z.boolean()
    .describe('true if the Cognito donor account was successfully re-enabled'),
});
export type ReAdmitDonorResponse = z.infer<typeof ReAdmitDonorResponseSchema>;


// ─── Discharge mother (dedicated endpoint) ────────────────────────────────────
// Accepts MOTHER and BOTH persons. Discharges the MOTHER role only.
// BOTH providers retain their donor role (person_type→DONOR).

export const DischargeMotherRequestSchema = z.object({
  hospital_id:      uuidSchema,
  discharge_reason: z.string().max(500).optional().nullable(),
  discharge_date:   z.string().datetime({ offset: true, local: true }).optional()
    .describe('Defaults to current time if omitted'),
});
export type DischargeMotherRequest = z.infer<typeof DischargeMotherRequestSchema>;

export const DischargeMotherResponseSchema = z.object({
  person_id:        uuidSchema,
  hospital_id:      uuidSchema,
  person_type:      z.string().describe('Original person_type — never mutated by discharge'),
  is_active:        z.boolean(),
  is_active_mother: z.boolean(),
  is_active_donor:  z.boolean(),
  discharged_at:    z.string().datetime(),
  discharge_reason: z.string().nullable().optional(),
  records_closed: z.object({
    milk_inventory_sent_home: z.array(z.object({
      receivable_id:                uuidSchema,
      receipt_number:               z.string(),
      volume_ml:                    z.number(),
      collected_at:                 z.string().datetime(),
      received_at:                  z.string().datetime(),
      expiry_at:                    z.string().datetime().nullable(),
      milk_type:                    z.string().nullable(),
      storage_condition_on_receipt: z.string(),
    })),
  }),
});
export type DischargeMotherResponse = z.infer<typeof DischargeMotherResponseSchema>;
